import config from "../config";
import events from "../constants/events";
import { ensureTrailingSlash } from "../helpers/url-helper";
import type {
  Attachment,
  CategorizationOption,
  ConversationDetails,
  ConversationHeader,
  CustomerContext,
  HttpError,
  Message,
} from "../types";
import {
  httpAsync,
  httpGet,
  httpGetBlob,
  httpPost,
  httpPostFile,
} from "./http";

const getStatusCode = (error: unknown): number | undefined => {
  if (typeof error === "number") {
    return error;
  }

  if (error && typeof error === "object" && "status" in error) {
    return (error as HttpError).status;
  }

  return undefined;
};

const onRequestFailed =
  <T>(defaultValueProvider: () => T) =>
  (error: unknown): T => {
    if (getStatusCode(error) === 401) {
      window.dispatchEvent(new Event(events.unauthorized));
    }

    return defaultValueProvider();
  };

const mapToAttachments = (attachment: Attachment): Attachment => ({
  attachmentId: attachment.attachmentId,
  reference: attachment.reference,
  name: attachment.name,
  size: attachment.size,
  contentType: attachment.contentType,
  status: attachment.status,
});

const stripHtml = (text: string | null | undefined): string => {
  if (text === null || text === undefined) {
    return "";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(text), "text/html");
  const content = doc?.body?.textContent ? doc.body.textContent : "";
  return content.replace(/\u00A0/g, " ");
};

const getNonEmptyLines = (text: string): string[] =>
  stripHtml(text)
    .split("\n")
    .filter((line) => !!line);

const parseSection = (section: string): string => {
  const sectionText = String(section ?? "");
  const items = getNonEmptyLines(sectionText);

  const parser = new DOMParser();
  const doc = parser.parseFromString(sectionText, "text/html");
  const header = doc.querySelector("h2");
  const headerText = header ? stripHtml(header.innerHTML).trim() : "";
  const hasHeader = !!headerText;

  return items
    .map((item, index) => {
      if (hasHeader && index === 0) {
        return item;
      }

      const number = hasHeader ? index : index + 1;
      return `${number}. ${item}\n`;
    })
    .join("\n");
};

const parsePrefilledReplySection = (prefilledReply: string): string =>
  prefilledReply
    .split("</section>")
    .map((section) => parseSection(section))
    .join("\n\n");

const parsePrefilledReplyListFormat = (prefilledReply: string): string =>
  getNonEmptyLines(prefilledReply)
    .map((item, counter) => `${counter + 1}. ${item}`)
    .join("\n\n");

const parsePrefilledReply = (prefilledReply: string): string =>
  prefilledReply.includes("</section>")
    ? parsePrefilledReplySection(prefilledReply)
    : parsePrefilledReplyListFormat(prefilledReply);

const mapToMessage = (message: {
  messageId: string;
  body: string;
  sender: string;
  prefilledReply: string;
  utcTimestamp: string;
  seenByCustomer: boolean;
  attachments: Attachment[];
}): Message => ({
  messageId: message.messageId,
  body: message.body,
  sender: message.sender,
  prefilledReply: parsePrefilledReply(message.prefilledReply),
  timestamp: new Date(message.utcTimestamp),
  seenByCustomer: message.seenByCustomer,
  attachments: message.attachments.map(mapToAttachments),
});

const mapToConversation = (conversation: {
  conversationId: string;
  subject: string;
  activityDate: string;
  isClosed: boolean;
  hasOneOrMoreUnseenMessages: boolean;
  claim: unknown;
}): ConversationHeader => ({
  conversationId: conversation.conversationId,
  subject: conversation.subject,
  activityDate: new Date(conversation.activityDate),
  isClosed: conversation.isClosed,
  hasOneOrMoreUnseenMessages: conversation.hasOneOrMoreUnseenMessages,
  claim: conversation.claim,
});

const parseConversations = (
  conversationsJson: string,
): ConversationHeader[] => {
  const conversationList = JSON.parse(conversationsJson) as Array<
    Parameters<typeof mapToConversation>[0]
  >;
  return conversationList.map(mapToConversation);
};

const parseConversation = (conversationJson: string): ConversationDetails => {
  const conversation = JSON.parse(conversationJson) as {
    conversationId: string;
    newestMessageId: string;
    messages: Array<Parameters<typeof mapToMessage>[0]>;
    conversationCategory?: string;
  };

  return {
    conversationId: conversation.conversationId,
    newestMessageId: conversation.newestMessageId,
    messages: conversation.messages.map(mapToMessage),
    conversationCategory: conversation.conversationCategory,
  };
};

const parseAttachmentResponse = (
  attachmentResponseJson: string,
): { AttachmentId: string } => {
  const attachmentResponse = JSON.parse(attachmentResponseJson) as {
    attachmentId: string;
  };

  return {
    AttachmentId: attachmentResponse.attachmentId,
  };
};

const parseCategorizationOptions = (
  categorizationResponseJson: string,
): CategorizationOption[] =>
  (JSON.parse(categorizationResponseJson) as CategorizationOption[]).map(
    (option) => ({
      name: option.name,
      context: option.context,
      categories: option.categories,
    }),
  );

const baseUrl = (customer: CustomerContext): string => {
  if (
    customer.baseUrl !== null &&
    customer.baseUrl !== undefined &&
    customer.baseUrl !== ""
  ) {
    return ensureTrailingSlash(customer.baseUrl);
  }

  return ensureTrailingSlash(config.secureMessagingApi.host);
};

const endpointUrl = (customer: CustomerContext, path: string): string =>
  `${baseUrl(customer)}${path}`;

export const getCultureCode = (customer: CustomerContext): Promise<string> =>
  httpGet(`${baseUrl(customer)}user/culture-code`, customer).catch(
    onRequestFailed(() => ""),
  );

export const getCategorizationOptions = (
  customer: CustomerContext,
): Promise<CategorizationOption[]> =>
  httpGet(`${baseUrl(customer)}message/categorization-options`, customer)
    .then(parseCategorizationOptions)
    .catch(onRequestFailed(() => []));

export const sendMessageAllowed = (
  customer: CustomerContext,
): Promise<boolean> =>
  httpGet(`${baseUrl(customer)}message/send-allowed`, customer)
    .then((jsonBoolean) => JSON.parse(jsonBoolean) as boolean)
    .catch(onRequestFailed(() => false));

export const messageSeen = (
  customer: CustomerContext,
  seenMessageId: string,
): Promise<string | {}> =>
  httpPost(`${baseUrl(customer)}message/seen/${seenMessageId}`, customer).catch(
    onRequestFailed(() => ({})),
  );

export const getConversationHeaders = (
  customer: CustomerContext,
  skip: number,
  take: number,
): Promise<ConversationHeader[]> =>
  httpGet(
    `${baseUrl(customer)}conversations/?numberOfConversationsToSkip=${skip}&numberOfConversationsToLoad=${take}`,
    customer,
  )
    .then(parseConversations)
    .catch(onRequestFailed(() => []));

export const getTotalNumberOfConversations = (
  customer: CustomerContext,
): Promise<number> =>
  httpGet(`${baseUrl(customer)}conversations/count`, customer)
    .then((jsonInteger) => JSON.parse(jsonInteger) as number)
    .catch(onRequestFailed(() => 0));

export const getConversation = (
  customer: CustomerContext,
  conversationId: string,
): Promise<ConversationDetails> =>
  httpGet(`${baseUrl(customer)}conversations/${conversationId}`, customer)
    .then(parseConversation)
    .catch(
      onRequestFailed(() => ({
        conversationId: "",
        newestMessageId: "",
        messages: [],
      })),
    );

export const startConversation = (
  customer: CustomerContext,
  context: string,
  categories: string[],
  subject: string,
  body: string,
  attachments: unknown[],
): Promise<string> =>
  httpPost(
    `${baseUrl(customer)}message/new-secure-message-conversation`,
    customer,
    JSON.stringify({
      Subject: subject,
      Body: body,
      Context: context,
      Categories: categories,
      Attachments: attachments,
    }),
  ).catch((error) => {
    throw error;
  });

export const sendReplyMessage = (
  customer: CustomerContext,
  conversationId: string,
  body: string,
  attachments: unknown[],
): Promise<string | {}> =>
  httpPost(
    `${baseUrl(customer)}message/respond`,
    customer,
    JSON.stringify({
      ConversationId: conversationId,
      Body: body,
      Attachments: attachments,
    }),
  ).catch(onRequestFailed(() => ({})));

export async function sendReplyMessageAsync(
  customer: CustomerContext,
  conversationId: string,
  body: string,
  attachments: unknown[],
): Promise<Response> {
  const url = endpointUrl(customer, "message/respond");

  const requestBody = JSON.stringify({
    ConversationId: conversationId,
    Body: body,
    Attachments: attachments,
  });

  return httpAsync.post(url, customer, requestBody);
}

export const sendFileAttachment = (
  customer: CustomerContext,
  formData: FormData,
  uploadId: string,
  onProgress: (percentage: number) => void,
): Promise<{ AttachmentId: string }> =>
  httpPostFile(
    `${baseUrl(customer)}attachment/upload/`,
    customer,
    formData,
    uploadId,
    onProgress,
  ).then(parseAttachmentResponse);

export const getFileAttachment = (
  customer: CustomerContext,
  attachmentId: string,
): Promise<Blob | false> =>
  httpGetBlob(`${baseUrl(customer)}attachment/${attachmentId}`, customer).catch(
    onRequestFailed(() => false),
  );

export { parsePrefilledReply, parseSection };
